import { ConfirmationDialog } from '@hooks/useConfirmationDialog';
import { Delete, EditOutlined, Restore, Save } from '@mui/icons-material';
import { Box, Button, Divider, Stack } from '@mui/material';
import {
  GridActionsCellItem,
  GridCellEditStopParams,
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridEditMode,
  GridFilterModel,
  GridRowClassNameParams,
  GridRowEditStopParams,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridRowSelectionModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridValidRowModel,
  MuiBaseEvent,
  MuiEvent,
} from '@mui/x-data-grid';
import { containsField, isEmptyObj } from '@utils/format';
import { hashData } from '@utils/security';
import localforage from 'localforage';
import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type TPromiseStatus = 'new' | 'edit' | 'exit' | 'exist' | null;

// Define a state model for rows (alias for now but can be extended later)
type StateModel<T extends GridValidRowModel> = T;

// Props for the datatable hook
type UseDatatableStateProps<T extends GridValidRowModel> = {
  isOpen: boolean;
  primaryKey: keyof T; // Primary key field name
  initialData?: StateModel<T>[]; // Initial data for the table
  showDialog?: boolean;
  autoAddRow?: boolean;
  initialFilterModel?: GridFilterModel;
  initialColumnVisibilityModel?: GridColumnVisibilityModel;
  forceColumnVisibilityModel?: GridColumnVisibilityModel;
  addRowButton?: StateModel<T>;
  processRow?: (data: T) => T;
  selectedRowsState?: [
    GridRowId[],
    React.Dispatch<React.SetStateAction<GridRowId[]>>,
  ];
  defaultClassName?: boolean;
  disableFilter?: boolean;
  multipleSelect?: boolean;
  processRowValidation?: (data: T) => boolean;
};

const findKeyWithAddButton = (obj) => {
  return Object.entries(obj).find(([, value]) => value === 'add-button')?.[0];
};

const isAddRowButtonRow = (
  row: GridValidRowModel,
  addRowButton?: GridValidRowModel,
) => {
  if (!addRowButton) return false;

  return Object.entries(addRowButton).every(
    ([key, value]) => row[key] === value,
  );
};

const KEYS = [
  'id',
  'referenceID',
  'referenceId',
  'refId',
  'refID',
  'checkId',
  'categoryId',
];

const getPrimaryKeyValue = (
  row: Record<string, any>,
  primaryKey: string | number | symbol,
): string | undefined => {
  for (const key of KEYS) {
    if (containsField(row, key)) {
      return row[key];
    }
  }

  return row[primaryKey as string];
};

const getInnerPK = (row: Record<string, any>): string | undefined => {
  for (const key of KEYS) {
    if (containsField(row, key)) {
      return key;
    }
  }

  return;
};

const normalizeValue = (value: any) =>
  typeof value === 'string' && !isNaN(+value) ? +value : value;

/**
 * Creates a specific localforage instance for managing grid state with custom parameters.
 * @returns {localforage.LocalForage} The localforage instance for grid state management.
 */
const createGridLocalForage = () => {
  return localforage.createInstance({
    driver: localforage.INDEXEDDB, // Use IndexedDB as the storage driver
    name: 'datatableGridStateDB', // Custom name for the database
    version: 1.0, // Version of the database
    storeName: 'gridStateStore', // Custom store name for the grid state
    description: 'Data table state storage', // Description of the store
  });
};

/**
 * Creates a multi-grid store for managing the state of multiple data tables.
 * @returns {Object} The multi-grid store with state management functions.
 */
const createMultiGridStore = () => {
  let listeners: Array<() => void> = [];
  const prefix = 'datatableState';
  const storeInstance = createGridLocalForage();

  /**
   * Generates a storage key for localforage based on the grid key and state type.
   * @param {string} gridKey - The unique key for the grid.
   * @param {'filter' | 'visibility'} stateType - The type of state to retrieve.
   * @returns {string} The storage key.
   */
  const getStorageKey = (gridKey: string, stateType: 'filter' | 'visibility') =>
    `${prefix}_${gridKey}_${stateType}`;

  const defaultState: {
    filterModel: GridFilterModel;
    columnVisibilityModel: GridColumnVisibilityModel;
  } = {
    filterModel: { items: [] },
    columnVisibilityModel: {},
  };

  /**
   * Retrieves the cached state from localforage or returns the default state.
   * @param {string} gridKey - The unique key for the grid.
   * @returns {Promise<Object>} The cached state containing filter and visibility models.
   */
  const getCachedState = async (gridKey: string) => {
    const filterModel =
      (await storeInstance.getItem<GridFilterModel>(
        getStorageKey(gridKey, 'filter'),
      )) || defaultState.filterModel;
    const columnVisibilityModel =
      (await storeInstance.getItem<GridColumnVisibilityModel>(
        getStorageKey(gridKey, 'visibility'),
      )) || defaultState.columnVisibilityModel;
    return { filterModel, columnVisibilityModel };
  };

  /**
   * Notifies all registered listeners of state changes.
   */
  const notifyListeners = () => {
    listeners.forEach((listener) => listener());
  };

  return {
    /**
     * Subscribes to state changes.
     * @param {Function} callback - The callback to be called on state change.
     * @returns {Function} A function to unsubscribe from state changes.
     */
    subscribe: (callback: () => void) => {
      listeners.push(callback);
      return () => {
        listeners = listeners.filter((listener) => listener !== callback);
      };
    },

    /**
     * Gets the current state snapshot for a specific grid key.
     * @param {string} gridKey - The unique key for the grid.
     * @returns {Promise<Object>} The current state snapshot.
     */
    getSnapshot: (gridKey: string) => getCachedState(gridKey),

    /**
     * Updates the state for a specific grid key and notifies listeners if changed.
     * @param {string} gridKey - The unique key for the grid.
     * @param {Object} state - The new state to update.
     * @param {GridFilterModel} [state.filterModel] - The new filter model.
     * @param {GridColumnVisibilityModel} [state.columnVisibilityModel] - The new column visibility model.
     * @returns {Promise<void>}
     */
    update: async (
      gridKey: string,
      state: {
        filterModel?: GridFilterModel;
        columnVisibilityModel?: GridColumnVisibilityModel;
      },
    ) => {
      const currentState = await getCachedState(gridKey);
      let hasChanged = false;
      if (state.filterModel) {
        const newFilterModel = JSON.stringify(state.filterModel);
        if (JSON.stringify(currentState.filterModel) !== newFilterModel) {
          await storeInstance.setItem(
            getStorageKey(gridKey, 'filter'),
            state.filterModel,
          );
          hasChanged = true;
        }
      }
      if (state.columnVisibilityModel) {
        const newVisibilityModel = JSON.stringify(state.columnVisibilityModel);
        if (
          JSON.stringify(currentState.columnVisibilityModel) !==
          newVisibilityModel
        ) {
          await storeInstance.setItem(
            getStorageKey(gridKey, 'visibility'),
            state.columnVisibilityModel,
          );
          hasChanged = true;
        }
      }
      if (hasChanged) {
        notifyListeners();
      }
    },
  };
};

/**
 * A custom hook for managing the state of a data table using the specific localforage instance.
 * @param {string} gridKey - The unique key for the grid.
 * @param {UseDatatableStateProps<T>} options - The options for the data table state.
 * @returns {Object} An object containing the filter model, column visibility model, and state update functions.
 */
const useControlledDatatable = <T extends GridValidRowModel>(
  gridKey: string,
  options: UseDatatableStateProps<T> = {
    isOpen: false,
    primaryKey: '' as keyof T,
    initialData: [],
    showDialog: false,
    initialFilterModel: { items: [] },
    initialColumnVisibilityModel: {},
    forceColumnVisibilityModel: {},
    addRowButton: undefined,
    processRow: undefined,
    selectedRowsState: undefined,
    defaultClassName: false,
    disableFilter: false,
    multipleSelect: false,
  },
) => {
  const {
    isOpen,
    primaryKey,
    initialData,
    showDialog,
    autoAddRow,
    initialFilterModel,
    initialColumnVisibilityModel,
    forceColumnVisibilityModel,
    addRowButton,
    processRow,
    selectedRowsState,
    defaultClassName,
    disableFilter,
    multipleSelect,
  } = options;

  // #region LOCALFORAGE DATA FOR FILTER AND COLUMN VISIBLE
  const store = useMemo(() => createMultiGridStore(), []);
  const [doneEdit, setDoneEdit] = useState(false);

  const [state, setState] = useState<{
    filterModel: GridFilterModel;
    columnVisibilityModel: GridColumnVisibilityModel;
  }>({
    filterModel: initialFilterModel || { items: [] },
    columnVisibilityModel: {
      ...(initialColumnVisibilityModel ?? {}),
      ...(forceColumnVisibilityModel ?? {}),
    },
  });

  useEffect(() => {
    (async () => {
      const newState = await store.getSnapshot(gridKey);
      setState({
        columnVisibilityModel: {
          ...initialColumnVisibilityModel,
          ...newState.columnVisibilityModel,
          ...forceColumnVisibilityModel,
          actions: true,
        },
        filterModel: {
          ...newState.filterModel,
          items: [
            ...(newState.filterModel.items ?? []),
            ...(initialFilterModel?.items ?? []),
          ],
        },
      });
    })();
  }, [isOpen]);

  useEffect(() => {
    const handleUpdate = async () => {
      const newState = await store.getSnapshot(gridKey);
      setState({
        columnVisibilityModel: newState.columnVisibilityModel,
        filterModel: newState.filterModel,
      });
    };

    const unsubscribe = store.subscribe(handleUpdate);
    return () => unsubscribe();
  }, [store, gridKey]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !multipleSelect) {
        resetRowModesToView();
        resetCellModesToView();
        handleFilterData(true);
      }
      // if (event.key === 'Enter') {
      //   resetRowModesToView(false);
      //   resetCellModesToView(false);
      // }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [multipleSelect]);

  const filterModel = useMemo(() => state.filterModel, [state.filterModel]);

  const columnVisibilityModel = useMemo(
    () => state.columnVisibilityModel,
    [state.columnVisibilityModel],
  );

  /**
   * Updates the state of the grid with the new filter or visibility model.
   * @param {Object} newState - The new state to update.
   * @param {GridFilterModel} [newState.filterModel] - The new filter model.
   * @param {GridColumnVisibilityModel} [newState.columnVisibilityModel] - The new column visibility model.
   */
  const updateState = useCallback(
    async (newState: {
      filterModel?: GridFilterModel;
      columnVisibilityModel?: GridColumnVisibilityModel;
    }) => {
      await store.update(gridKey, newState);
    },
    [store, gridKey],
  );

  /**
   * Handles changes to the filter model.
   * @param {GridFilterModel} newFilterModel - The new filter model.
   */
  const handleFilterModelChange = useCallback(
    async (newFilterModel: GridFilterModel) => {
      await updateState({ filterModel: newFilterModel });
    },
    [updateState],
  );

  /**
   * Handles changes to the column visibility model.
   * @param {GridColumnVisibilityModel} newColumnVisibilityModel - The new column visibility model.
   */
  const handleColumnVisibilityModelChange = useCallback(
    async (newColumnVisibilityModel: GridColumnVisibilityModel) => {
      await updateState({ columnVisibilityModel: newColumnVisibilityModel });
    },
    [updateState],
  );

  /**
   * Resets the filter and column visibility models to their initial values.
   */
  const resetState = useCallback(async () => {
    const resetData = {
      filterModel: initialFilterModel || { items: [] },
      columnVisibilityModel: {
        ...(initialColumnVisibilityModel ?? {}),
        ...(forceColumnVisibilityModel ?? {}),
      },
    };

    setState(resetData);
    await store.update(gridKey, resetData);
  }, [
    store,
    gridKey,
    initialFilterModel,
    initialColumnVisibilityModel,
    forceColumnVisibilityModel,
  ]);
  // #endregion

  // #region HANDLING ONE CLICK TO CELL EDIT MODE
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});

  /**
   * Handles cell click events to enable editing mode.
   * @param {GridCellParams} params - The parameters of the clicked cell.

   */
  const handleCellClick = useCallback(
    async (params: GridCellParams) => {
      if (!params?.isEditable) return;
      if (!params?.isEditable && isOpen && showDialog) {
        return setPromiseArguments(() => ({
          resolve: handleNo,
          reject: handleContinueEditing,
          newRow: params.row,
          oldRow: params.row,
          status: 'exit',
        }));
      }

      if (addRowButton) {
        setDatatableData((prevData) =>
          prevData.filter((row) => !isAddRowButtonRow(row, addRowButton)),
        );
      }

      await new Promise<void>((resolve) => {
        setTimeout(() => {
          setCellModesModel((prevModel) => ({
            ...prevModel,
            [params.id]: {
              ...prevModel[params.id],
              [params.field]: { mode: GridCellModes.Edit },
            },
          }));
          resolve();
        }, 0);
      });
    },
    [addRowButton],
  );

  /**
   * Handles changes to the cell modes model.
   * @param {GridCellModesModel} newModel - The new cell modes model.
   */
  const handleCellModesModelChange = useCallback(
    (newModel: GridCellModesModel) => {
      setCellModesModel(newModel);
    },
    [],
  );

  /**
   * Sets a specific cell to view mode.
   * @param {string} id - The ID of the cell to set to view mode.
   */
  const setCellToViewMode = (id: string) => {
    setCellModesModel((prevModel) => {
      return {
        ...prevModel,
        [id]: {
          ...prevModel[id],
          [Object.keys(prevModel[id] || {})[0]]: { mode: GridCellModes.View },
        },
      };
    });
  };

  /**
   * Resets all cell modes to view mode.
   */
  const resetCellModesToView = (forceReset: boolean = true) => {
    if (forceReset) {
      return setCellModesModel({});
    }
    setCellModesModel((prevModel) => {
      const updatedModel = Object.keys(prevModel).reduce((acc, id) => {
        if (id === '' || id === '-') return acc;

        acc[id] = Object.keys(prevModel[id] || {}).reduce((fieldAcc, field) => {
          fieldAcc[field] = { mode: GridCellModes.View };
          return fieldAcc;
        }, {});

        return acc;
      }, {});
      return updatedModel;
    });
  };

  // #endregion

  // #region HANDLE ONE CLICK TO ROW EDIT MODE
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  /**
   * Handles the start of row editing mode.
   * @param {GridCellParams} params - The parameters of the clicked cell.
   */
  const handleRowEditStart = useCallback(
    async (params: GridRowParams) => {
      const isAddButton = Object.values(params.row).includes('add-button');
      const isEditable = params.columns.some((cell) => cell.editable);
      const editRowIds = Object.keys(rowModesModel || {});

      if (
        !isEditable ||
        isAddButton ||
        (!multipleSelect && editRowIds.length >= 1)
      )
        return;

      if (!isEditable && isOpen && showDialog) {
        setPromiseArguments({
          resolve: handleNo,
          reject: handleContinueEditing,
          newRow: params.row,
          oldRow: params.row,
          status: 'exit',
        });
        return;
      }

      if (addRowButton) {
        setDatatableData((prevData) =>
          prevData.filter((row) => !isAddRowButtonRow(row, addRowButton)),
        );
      }

      await new Promise<void>((resolve) => {
        setTimeout(() => {
          setRowModesModel((prevModel) => {
            if (multipleSelect) {
              return {
                ...prevModel,
                [params.id]: { mode: GridRowModes.Edit },
              };
            } else {
              return {
                [params.id]: { mode: GridRowModes.Edit },
              };
            }
          });
          resolve();
        }, 0);
      });
    },
    [rowModesModel, isOpen, showDialog, multipleSelect, addRowButton],
  );

  /**
   * Manually starts row editing mode for a specific row.
   * @param {string} id - The ID of the row to edit.
   * @param {string} fieldToFocus - The field to focus on during editing.
   */
  const handleManualRowEditStart = useCallback(
    (id: string, fieldToFocus: string) => {
      if (addRowButton) {
        setDatatableData((prevData) =>
          prevData.filter((row) => !isAddRowButtonRow(row, addRowButton)),
        );
      }

      setRowModesModel((prevModel) => ({
        ...prevModel,
        [id]: {
          mode: GridRowModes.Edit,
          fieldToFocus,
        },
      }));
    },
    [addRowButton],
  );

  /**
   * Handles changes to the row modes model.
   * @param {GridRowModesModel} newModel - The new row modes model.
   */
  const handleRowModesModelChange = useCallback(
    (newModel: GridRowModesModel) => {
      setRowModesModel(newModel);
    },
    [],
  );

  /**
   * Resets all row modes to view mode.
   */
  const resetRowModesToView = (
    forceReset: boolean = true,
    ignoreModifications: boolean = false,
  ) => {
    if (forceReset) {
      return setRowModesModel({});
    }
    setRowModesModel((prevModel) => {
      const updatedModel = Object.keys(prevModel).reduce((acc, id) => {
        if (id === '' || id === '-') return acc;

        acc[id] = {
          mode: GridRowModes.View,
          ...(ignoreModifications && { ignoreModifications: true }),
        };
        return acc;
      }, {});
      return updatedModel;
    });
  };
  // #endregion

  // #region EDITING STATE
  const areArraysEqual = (arr1: StateModel<T>[], arr2: StateModel<T>[]) => {
    if (arr1.length !== arr2.length) return false;

    return arr1.every((item, index) => {
      const keys1 = Object.keys(item);
      const keys2 = Object.keys(arr2[index]);

      if (keys1.length !== keys2.length) return false;

      return keys1.every((key) => item[key] === arr2[index][key]);
    });
  };

  const initial = initialData ?? [];
  const withAddButton = addRowButton ? [...initial, addRowButton] : initial;
  const [datatableData, setDatatableData] =
    useState<StateModel<T>[]>(withAddButton);

  const [editedDatatableData, setEditedDatatableData] = useState<
    StateModel<T>[]
  >([]);

  const [deletedDatatableData, setDeletedDatatableData] = useState<
    StateModel<T>[]
  >([]);

  const autoAddRowIdsRef = useRef<Set<string>>(new Set());

  const [originalDatatableData, setOriginalDatatableData] =
    useState<StateModel<T>[]>(initial);

  const [promiseArguments, setPromiseArguments] = useState<{
    resolve: (row: StateModel<T>) => void;
    reject: (row: StateModel<T>) => void;
    newRow: StateModel<T>;
    oldRow: StateModel<T>;
    status: TPromiseStatus;
  } | null>(null);

  useEffect(() => {
    if (!areArraysEqual(originalDatatableData, initial)) {
      setOriginalDatatableData(initial);
    }
  }, [initial, originalDatatableData]);

  /**
   * Processes row updates and checks for validation.
   * @param {StateModel<T>} newRow - The new row data.
   * @param {StateModel<T>} oldRow - The old row data.
   * @returns {Promise<StateModel<T>>} A promise that resolves with the old row if validation passes.
   */
  const processRowUpdate = useCallback(
    (newRow: StateModel<T>, oldRow: StateModel<T>) =>
      new Promise<StateModel<T>>((resolve, reject) => {
        const primaryKeyValue = getPrimaryKeyValue(newRow, primaryKey);

        const innerPK = getInnerPK(newRow) || primaryKey;

        const isEqual = Object.keys(newRow).every((key) => {
          if (key === 'id') return true;
          return normalizeValue(newRow[key]) === normalizeValue(oldRow[key]);
        });

        const isOnlyIdFilled =
          Object.keys(newRow).every(
            (key) =>
              key === 'id' ||
              newRow[key] === '' ||
              newRow[key] === 0 ||
              newRow[key] === null ||
              newRow[key] === undefined,
          ) || isEqual;

        const isNotValid =
          primaryKeyValue === '' ||
          primaryKeyValue === null ||
          primaryKeyValue === undefined ||
          isOnlyIdFilled;

        const isHaveDuplicateId = datatableData.some(
          (row) =>
            row[innerPK] === newRow[innerPK] &&
            row[innerPK] !== oldRow[innerPK],
        );

        const isExistData = datatableData.some(
          (row) =>
            row[innerPK] === oldRow[innerPK] &&
            oldRow[innerPK] !== '' &&
            !isHaveDuplicateId &&
            !isOnlyIdFilled,
        );

        const getStatus = (): { status: TPromiseStatus } => {
          if (isNotValid) return { status: 'exit' };
          if (isHaveDuplicateId) return { status: 'exist' };
          if (isExistData) return { status: 'edit' };
          return { status: 'new' };
        };

        const { status } = getStatus();

        if (!showDialog) {
          return handleYes({
            manualStatus: {
              resolve,
              reject,
              newRow: { ...newRow, _action: status },
              oldRow,
              status,
            },
          });
        } else if (status) {
          setPromiseArguments({
            resolve,
            reject,
            newRow: { ...newRow, _action: status },
            oldRow,
            status,
          });
        } else {
          resolve(oldRow);
        }
      }),
    [datatableData, primaryKey, showDialog, doneEdit],
  );

  /**
   * Filters the data to remove rows with incomplete fields.
   */
  const handleFilterData = (removeLastItem?: boolean) => {
    setDatatableData((prevData) => {
      let filteredData: typeof prevData;

      if (removeLastItem) {
        filteredData = [...prevData];
        filteredData.pop();
      } else {
        filteredData = disableFilter
          ? [...prevData]
          : prevData.filter((row) => {
              const processedRow = processRow?.(row) ?? row;

              return Object.entries(processedRow).some(
                ([key, value]) =>
                  key === primaryKey &&
                  value !== '' &&
                  value !== null &&
                  value !== undefined,
              );
            });
      }

      // Ensure addRowButton is always last (if defined)
      if (addRowButton) {
        // Remove existing occurrences of addRowButton
        filteredData = filteredData.filter(
          (row) => !isAddRowButtonRow(row, addRowButton),
        );

        // Push it to the end
        filteredData.push(addRowButton);
      }

      return filteredData;
    });
  };

  /**
   * Adds new items to the data table.
   * @param {Partial<T>} newRowData - The new row data to add.
   */
  const handleAddItems = async (newRowData: Partial<T>) => {
    const date = Date.now();
    const rowId = `${date}`;
    const addRowDefaults = addRowButton
      ? (Object.entries(addRowButton).reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: value === 'add-button' ? '' : value,
          }),
          {},
        ) as Partial<T>)
      : {};

    if (autoAddRow) {
      autoAddRowIdsRef.current.add(rowId);
    }

    await new Promise<void>((resolve) => {
      setDatatableData((oldRows: StateModel<T>[]) => {
        // Remove any rows where a field has 'add-button'
        const filteredData = oldRows.filter(
          (row) => !Object.values(row).some((value) => value === 'add-button'),
        );

        // Ensure the new row has a unique key
        const newRow = {
          ...addRowDefaults,
          ...newRowData,
          _action: 'raw',
          id: rowId,
        } as unknown as StateModel<T>;

        // Check for duplicate keys and ensure uniqueness
        const uniqueData = filteredData.filter((row) => row.id !== newRow.id);

        // Set the updated state and resolve the Promise
        resolve(); // Important! Resolve after state update
        return [...uniqueData, newRow];
      });
    });

    await new Promise<void>((resolve) =>
      setTimeout(() => {
        handleManualRowEditStart(
          rowId,
          addRowButton
            ? findKeyWithAddButton(addRowButton) || (primaryKey as string)
            : (primaryKey as string),
        );
        resolve();
      }, 0),
    );
  };

  /**
   * Handles the action when the user selects "No" in the confirmation dialog.
   */
  const handleNo = () => {
    if (promiseArguments) {
      const { oldRow, resolve } = promiseArguments;
      resolve(oldRow);
      resetRowModesToView();
      resetCellModesToView();
      setPromiseArguments(null);
      handleFilterData();
    }
  };

  /**
   * Handles the confirmation action when the user selects "Yes" in the confirmation dialog.
   * This function updates the data table based on the provided promise arguments or a manual status.
   * It updates both the main data table and the edited data state.
   *
   * @param {TPromiseStatus} [manualStatus] - Optional status indicating the type of action ('new', 'edit', 'exist').
   * If provided, this status takes precedence over the promise arguments.
   * @returns {Promise<void>} A promise that resolves when the operation is complete.
   * If the operation fails, it rejects with the old row data.
   */
  const handleYes = useCallback(
    async ({
      manualStatus,
    }: {
      manualStatus?: {
        resolve: (row: StateModel<T>) => void;
        reject: (row: StateModel<T>) => void;
        newRow: StateModel<T>;
        oldRow: StateModel<T>;
        status: TPromiseStatus;
      };
    }) => {
      /**
       * Updates the rows in the data table based on the provided status.
       *
       * @param {Array<GridValidRowModel>} prevRows - The previous rows of the data table.
       * @param {GridValidRowModel} newRow - The new row data to be added or updated.
       * @param {GridValidRowModel} oldRow - The old row data to be replaced if necessary.
       * @param {TPromiseStatus} status - The status indicating the type of action ('new', 'edit', 'exist').
       * @returns {Array<GridValidRowModel>} The updated rows of the data table.
       */
      const updateRows = (
        prevRows: T[],
        rawNewRow: T,
        oldRow: T,
        status: TPromiseStatus,
      ) => {
        const newRow = processRow ? processRow(rawNewRow) : rawNewRow;

        const innerPK =
          getInnerPK(newRow) === getInnerPK(oldRow)
            ? getInnerPK(newRow) || primaryKey
            : primaryKey;

        switch (status) {
          case 'edit':
            return prevRows.map((row) =>
              row[innerPK] === newRow[innerPK] ||
              row[innerPK] === oldRow[innerPK]
                ? { ...row, ...newRow }
                : row,
            );
          case 'exist':
            return prevRows
              .filter((row) => row[innerPK] !== oldRow[innerPK])
              .map((row) => (row[innerPK] === newRow[innerPK] ? newRow : row));
          case 'new':
            return [...prevRows, newRow];
          default:
            return prevRows;
        }
      };

      /**
       * Updates the edited rows state based on the provided status.
       *
       * @param {Array<GridValidRowModel>} prevEditedRows - The previous edited rows.
       * @param {GridValidRowModel} newRow - The new row data to be added or updated.
       * @param {GridValidRowModel} oldRow - The old row data to be replaced if necessary.
       * @param {TPromiseStatus} status - The status indicating the type of action ('new', 'edit', 'exist').
       * @returns {Array<GridValidRowModel>} The updated edited rows.
       */
      const updateEditedRows = (
        prevEditedRows: T[],
        rawNewRow: T,
        oldRow: T,
        status: TPromiseStatus,
      ) => {
        const newRow = processRow ? processRow(rawNewRow) : rawNewRow;

        const innerPK =
          getInnerPK(newRow) === getInnerPK(oldRow)
            ? getInnerPK(newRow) || primaryKey
            : primaryKey;

        const isAlreadyEdited = prevEditedRows.some(
          (row) => row[innerPK] === newRow[innerPK],
        );

        switch (status) {
          // REMOVE NEW ITEM ONLY EDITED WILL INSERT HERE
          // case 'new':
          //   return [...prevEditedRows, newRow];
          case 'edit':
            return isAlreadyEdited
              ? prevEditedRows.map((row) =>
                  row[innerPK] === newRow[innerPK] ||
                  row[innerPK] === oldRow[innerPK]
                    ? { ...row, ...newRow }
                    : row,
                )
              : [...prevEditedRows, newRow];
          case 'exist':
            return prevEditedRows
              .filter((row) => row[innerPK] !== oldRow[innerPK])
              .map((row) => (row[innerPK] === newRow[innerPK] ? newRow : row));
          default:
            return prevEditedRows;
        }
      };

      if (manualStatus) {
        const { newRow, oldRow, status, resolve } = manualStatus;
        const autoAddRowId = oldRow.id?.toString() || newRow.id?.toString();
        const shouldAutoAddRow =
          autoAddRow &&
          status !== 'exit' &&
          !!autoAddRowId &&
          autoAddRowIdsRef.current.has(autoAddRowId);

        if (autoAddRowId) {
          autoAddRowIdsRef.current.delete(autoAddRowId);
        }

        const isAllFieldsEmpty = Object.keys(newRow).every(
          (item) =>
            newRow[item] === '' ||
            newRow[item] === null ||
            newRow[item] === undefined,
        );

        if (status === 'exit') {
          resolve(oldRow); // old reject
          await new Promise<void>((innerResolve) =>
            setTimeout(() => {
              // if (!doneEdit) {
              resetRowModesToView();
              resetCellModesToView();
              // }
              handleFilterData();
              innerResolve();
            }, 0),
          );
          return;
        }

        if (isAllFieldsEmpty) {
          resolve(newRow);
          await new Promise<void>((innerResolve) =>
            setTimeout(() => {
              // if (!doneEdit) {
              resetRowModesToView();
              resetCellModesToView();
              // }
              setDatatableData((prev) =>
                prev.filter((item) => item[primaryKey] !== oldRow[primaryKey]),
              );
              setEditedDatatableData((prev) =>
                prev.filter((item) => item[primaryKey] !== oldRow[primaryKey]),
              );
              innerResolve();
            }, 0),
          );
          return;
        }
        setDatatableData((prevRows) =>
          updateRows(prevRows, newRow, oldRow, status),
        );
        setEditedDatatableData((prevEditedRows) =>
          updateEditedRows(prevEditedRows, newRow, oldRow, status),
        );
        handleFilterData();

        // if (!doneEdit) {
        resetRowModesToView();
        resetCellModesToView();
        // }

        resolve(newRow);

        if (shouldAutoAddRow) {
          handleAddItems({} as Partial<T>);
        }
        return;
      }

      if (!promiseArguments) return;

      const { newRow, oldRow, resolve, reject, status } = promiseArguments;

      try {
        resolve(newRow);
        setDatatableData((prevRows) =>
          updateRows(prevRows, newRow, oldRow, status),
        );
        setEditedDatatableData((prevEditedRows) =>
          updateEditedRows(prevEditedRows, newRow, oldRow, status),
        );
        handleFilterData();
        resetRowModesToView();
        resetCellModesToView();
        setPromiseArguments(null);
      } catch (error) {
        handleFilterData();
        resetRowModesToView();
        resetCellModesToView();
        reject(oldRow); // Reject the promise with the old row data if an error occurs.
        setPromiseArguments(null);
      } finally {
        setDoneEdit(false);
      }
    },
    [
      promiseArguments,
      primaryKey,
      datatableData,
      editedDatatableData,
      rowModesModel,
      cellModesModel,
      doneEdit,
    ],
  );

  /**
   * Continues editing the row when the user opts to keep editing.
   */
  const handleContinueEditing = async () => {
    if (promiseArguments) {
      const { newRow, reject } = promiseArguments;
      reject(newRow);
      setPromiseArguments(null);
      handleManualRowEditStart('', primaryKey as string);
    }
  };

  /**
   * Handles the stopping of row editing in the data grid.
   * This function is triggered when a row edit is stopped.
   *
   * @param {GridRowEditStopParams} params - The parameters associated with the row edit stop event.
   * @param {MuiEvent<MuiBaseEvent>} _event - The event object related to the row edit stop.
   *
   * @returns {void}
   *
   * @example
   * // Example usage of handleRowEditStop
   * handleRowEditStop({ reason: 'escapeKeyDown' }, event);
   */
  const handleRowEditStop = async (
    params: GridRowEditStopParams,
    event: MuiEvent<MuiBaseEvent>,
  ) => {
    const { reason } = params;
    if (reason === 'rowFocusOut') {
      event.defaultMuiPrevented = true;
      return;
    }
    // if (reason === 'escapeKeyDown') {
    //   await new Promise<void>((resolve) =>
    //     setTimeout(() => {
    //       resetRowModesToView();
    //       resetCellModesToView();
    //       handleFilterData();
    //       resolve();
    //     }, 0),
    //   );
    //   return;
    // }
  };

  const handleCellEditStop = async (
    params: GridCellEditStopParams,
    _event: MuiEvent<MuiBaseEvent>,
  ) => {
    const { reason } = params;
    // if (reason === 'cellFocusOut') {
    //   event.defaultMuiPrevented = true;
    //   return;
    // }
    if (reason === 'escapeKeyDown') {
      await new Promise<void>((resolve) =>
        setTimeout(() => {
          resetRowModesToView();
          resetCellModesToView();
          handleFilterData();
          resolve();
        }, 0),
      );
      return;
    }
  };

  /**
   * Determines if a cell is editable based on the current editing state.
   * A cell is editable if:
   * - The cell's ID matches the ID of the row currently being edited, or
   * - There are no active editing modes (i.e., the grid is in its default state).
   *
   * @param {GridCellParams} params - The parameters for the grid cell, including the cell's ID.
   * @returns {boolean} True if the cell is editable, otherwise false.
   */
  const isCellEditable = useCallback(
    (params: GridCellParams) => {
      const rowId = params.id;

      if (rowId === 'add-button') return false;

      if (multipleSelect) {
        return !!rowModesModel[rowId];
      }

      const editRowIds = Object.keys(rowModesModel || {});

      return (
        (editRowIds.length <= 1 && editRowIds[0] === rowId) ||
        isEmptyObj(rowModesModel)
      );
    },
    [rowModesModel, cellModesModel, datatableData, primaryKey, multipleSelect],
  );

  /**
   * Checks if all rows in the data table are valid.
   * A row is considered valid if all of its values are not empty, null, undefined, or a placeholder ('-').
   *
   * @returns {boolean} True if all rows in the data table are valid, otherwise false.
   */
  const isDatatableDataValid = useCallback(() => {
    return datatableData.every((row) => {
      const primaryKeyValue = row[primaryKey];

      return (
        primaryKeyValue !== '' &&
        primaryKeyValue !== null &&
        primaryKeyValue !== undefined
      );
    });
  }, [datatableData, primaryKey]);

  /**
   * Renders the confirmation dialog based on the current promise arguments.
   * @returns {JSX.Element|null} The confirmation dialog component or null if not needed.
   */
  const renderConfirmDialog = useMemo(() => {
    if (!promiseArguments || !isOpen) return null;

    const { status } = promiseArguments;

    return (
      <ConfirmationDialog
        open={!!promiseArguments}
        title={
          status === 'edit'
            ? 'Save Transaction'
            : status === 'new'
              ? 'New Transaction'
              : status === 'exist'
                ? 'Overwrite Transaction'
                : 'Cancel Transaction'
        }
        desc={
          status === 'edit'
            ? 'Do you want to save the changes made?'
            : status === 'new'
              ? 'Do you want to add the new item to the table?'
              : status === 'exist'
                ? 'This will overwrite the existing transaction. Do you want to proceed?'
                : 'Some fields are incomplete. Would you like to keep editing?'
        }
        color='warning'
        iconColor='warning'
        onConfirm={
          status === 'exit' ? handleContinueEditing : async () => handleYes({})
        }
        onClose={handleNo}
        confirmButtonTitle='Yes'
        cancelButtonTitle='No'
      />
    );
  }, [promiseArguments, isOpen, handleYes]);

  // #endregion

  // #region ROW SELECTION &  ACTION COLUMN
  const [selectedRows, setSelectedRows] =
    selectedRowsState || useState<GridRowId[]>([]);

  const isRowSelectable = useMemo(
    (custom?: boolean) => {
      return (params: GridRowParams) => {
        const id = String(params.id);

        const isDeleted = deletedDatatableData.some((item) => {
          const dataID = getInnerPK(item) || primaryKey;
          return id.includes(item[dataID]);
        });
        return custom || !isDeleted;
      };
    },
    [deletedDatatableData],
  );

  const handleSelectionChange = useCallback(
    (selectionModel: GridRowSelectionModel) => {
      setSelectedRows(selectionModel);
    },
    [],
  );

  const clearSelectedRows = useCallback(() => {
    setSelectedRows([]);
  }, []);

  const discardAllChanges = useCallback(() => {
    const restored = originalDatatableData;
    setDatatableData(addRowButton ? [...restored, addRowButton] : restored);
    setEditedDatatableData([]);
    setDeletedDatatableData([]);
  }, [originalDatatableData, addRowButton]);

  const updateChanges = useCallback(async () => {
    const updated = editedDatatableData.filter(
      (row) => row._action !== 'delete',
    );
    const deleted = deletedDatatableData;

    const combined = [
      ...originalDatatableData,
      ...datatableData.filter((row) => {
        const primaryKeyValue = getPrimaryKeyValue(row, primaryKey);
        const originalKeyList = originalDatatableData.map((r) =>
          getPrimaryKeyValue(r, primaryKey),
        );
        return !originalKeyList.includes(primaryKeyValue);
      }),
    ];

    const updatedOriginal: T[] = combined
      .filter((row) => {
        const primaryKeyValue = getPrimaryKeyValue(row, primaryKey);
        return !deleted.some((d) => {
          const dPrimaryKey = getPrimaryKeyValue(d, primaryKey);
          return dPrimaryKey === primaryKeyValue;
        });
      }) // remove deleted
      .map((row) => {
        const primaryKeyValue = getPrimaryKeyValue(row, primaryKey);
        const updatedRow = updated.find((u) => {
          const uPrimaryKey = getPrimaryKeyValue(u, primaryKey);
          return uPrimaryKey === primaryKeyValue;
        });
        // Return the updated row or the original row with _action removed
        const updatedRowWithoutAction = updatedRow
          ? { ...updatedRow, _action: undefined } // Remove _action from updated row
          : row;
        const { _action, ...rowWithoutAction } = updatedRowWithoutAction; // Remove _action field if any
        return rowWithoutAction;
      }) as T[]; // Type assertion to T[]

    // Filter out any row with the key holding 'add-button' as a value
    const filteredOriginal = updatedOriginal.filter((row) => {
      const keyWithAddButton = findKeyWithAddButton(row);
      return (
        keyWithAddButton === undefined || row[keyWithAddButton] !== 'add-button'
      );
    });

    // Set the filtered original data
    setOriginalDatatableData(filteredOriginal);
    setDatatableData(updatedOriginal);
    setEditedDatatableData([]);
    setDeletedDatatableData([]);
  }, [
    editedDatatableData,
    deletedDatatableData,
    originalDatatableData,
    datatableData,
    addRowButton,
    primaryKey,
  ]);

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams<any>) => {
      const isOdd =
        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';

      if (defaultClassName) return isOdd;

      const rowId = params.id.toString();
      const isAddButton = rowId === 'add-button' || rowId === '';

      const isEdited = editedDatatableData.some((item) => {
        const dataID = getInnerPK(item) || primaryKey;
        return rowId.includes(item[dataID]) && !isAddButton;
      });
      const isNew = originalDatatableData.every((item) => {
        const dataID = getInnerPK(item) || primaryKey;
        return !rowId.includes(item[dataID]) && !isAddButton;
      });

      const isDeleted = deletedDatatableData.some((item) => {
        const dataID = getInnerPK(item) || primaryKey;
        return rowId.includes(item[dataID]) && !isAddButton;
      });

      return [
        isOdd,
        isNew && !isEdited && !isDeleted && !isAddButton ? 'new' : '',
        isEdited ? 'edited' : '',
        isDeleted ? 'deleted' : '',
        isAddButton ? 'disable-checkbox' : '',
      ]
        .filter(Boolean)
        .join(' ');
    },
    [
      editedDatatableData,
      originalDatatableData,
      deletedDatatableData,
      primaryKey,
      defaultClassName,
    ],
  );

  const originalRowMap = useMemo(() => {
    return new Map(
      originalDatatableData?.map((row) => {
        const primaryKeyValue = getPrimaryKeyValue(row, primaryKey);
        return [primaryKeyValue, row];
      }),
    );
  }, [originalDatatableData, primaryKey]);

  // FUNCTION FOR ACTION COLUMN
  const handleDiscardChanges = useCallback(
    (id: string, dataID: string | number | symbol) => {
      const originalRow = originalDatatableData.find((item) =>
        id?.toString().includes(item[dataID]?.toString()),
      );

      if (originalRow) {
        setDatatableData((prev) =>
          prev.map((row) =>
            id?.toString().includes(row[dataID]?.toString())
              ? originalRow
              : row,
          ),
        );
      }

      setEditedDatatableData((prev) =>
        prev.filter((row) => !id?.toString().includes(row[dataID]?.toString())),
      );

      setDeletedDatatableData((prev) =>
        prev.filter((row) => !id?.toString().includes(row[dataID]?.toString())),
      );
    },
    [
      originalDatatableData,
      setDatatableData,
      setEditedDatatableData,
      setDeletedDatatableData,
    ],
  );

  const handleDelete = useCallback(
    (id: string, dataID: string | number | symbol, rawRow: object) => {
      const row = datatableData.find((r) =>
        id?.toString().includes(r[dataID]?.toString()),
      );

      const isNew = () => {
        const primaryKeyValue = getPrimaryKeyValue(rawRow, primaryKey);

        return !originalRowMap.has(primaryKeyValue);
      };

      if (row) {
        if (isNew()) {
          // If it's a new row, just remove it immediately
          setDatatableData((prev) =>
            prev.filter(
              (item) => !id?.toString().includes(item[dataID]?.toString()),
            ),
          );
        } else {
          // If it's an existing row, mark as deleted
          setDatatableData((prev) =>
            prev.map((item) =>
              id?.toString().includes(item[dataID]?.toString())
                ? { ...item, _action: 'delete' }
                : item,
            ),
          );

          setDeletedDatatableData((prev) => {
            const already = prev.find((r) => r[dataID] === row[dataID]);
            return already ? prev : [...prev, { ...row, _action: 'delete' }];
          });
        }

        setEditedDatatableData((prev) =>
          prev.filter((r) => !id?.toString().includes(r[dataID]?.toString())),
        );
      }
    },
    [
      datatableData,
      setDatatableData,
      setDeletedDatatableData,
      setEditedDatatableData,
      getPrimaryKeyValue,
      primaryKey,
      originalRowMap,
    ],
  );

  const actionColumn = useMemo<GridColDef>(
    () => ({
      field: 'actions',
      headerName: '',
      type: 'actions',
      minWidth: 70,
      headerClassName: 'no-padding',
      hideable: false,

      getActions: (params: GridRowParams) => {
        const id = String(params.id);
        if (id === 'add-button' || id === '') return [];
        const hasSelected = selectedRows.length > 0;

        const dataID = getInnerPK(params.row) || primaryKey;

        const isEdited = editedDatatableData.some((item) =>
          id?.includes(item[dataID]?.toString()),
        );

        const isDeleted = deletedDatatableData.some((item) =>
          id?.includes(item[dataID]?.toString()),
        );

        const isNew = () => {
          const row = params.row;
          const primaryKeyValue = getPrimaryKeyValue(row, primaryKey);

          return !originalRowMap.has(primaryKeyValue);
        };

        const onEdit = rowModesModel.hasOwnProperty(id);

        return [
          <GridActionsCellItem
            icon={<Restore />}
            label='Discard changes'
            disabled={onEdit || !isEdited || isNew()}
            onClick={() => handleDiscardChanges(id, dataID)}
            sx={{ bgcolor: 'transparent' }}
          />,
          <GridActionsCellItem
            icon={<Delete />}
            label='Delete'
            disabled={onEdit || hasSelected || isDeleted}
            onClick={() => handleDelete(id, dataID, params.row)}
            sx={{ bgcolor: 'transparent' }}
          />,
        ];
      },
    }),
    [
      rowModesModel,
      datatableData,
      editedDatatableData,
      deletedDatatableData,
      originalDatatableData,
      primaryKey,
      selectedRows,
    ],
  );

  // FUNCTION FOR ACTION TOOLBAR
  const handleDeleteRows = useCallback(() => {
    const rowsToDelete = datatableData.filter((row) => {
      const dataID = getInnerPK(row) || primaryKey;

      return selectedRows.includes(String(row[dataID]));
    });

    const isNew = (row: object) => {
      const primaryKeyValue = getPrimaryKeyValue(row, primaryKey);

      return !originalRowMap.has(primaryKeyValue);
    };

    rowsToDelete.forEach((row) => {
      const isNewRow = isNew(row);
      const dataID = getInnerPK(row) || primaryKey;
      const rowID = row[dataID];

      if (isNewRow) {
        // If it's a new row, just remove it immediately from datatableData
        setDatatableData((prev) =>
          prev.filter((item) => item[dataID] !== rowID),
        );
      } else {
        // If it's an existing row, mark it as deleted
        setDatatableData((prev) =>
          prev.map((item) =>
            item[dataID] === rowID ? { ...item, _action: 'delete' } : item,
          ),
        );

        // Add to deletedDatatableData with _action: 'delete'
        setDeletedDatatableData((prev) => {
          const alreadyDeleted = prev.some((r) => r[dataID] === rowID);
          return alreadyDeleted
            ? prev
            : [...prev, { ...row, _action: 'delete' }];
        });
      }

      // Remove the deleted row from editedDatatableData as well
      setEditedDatatableData((prev) => prev.filter((r) => r[dataID] !== rowID));
    });

    // Clear selected rows after deletion
    setSelectedRows([]);
  }, [
    datatableData,
    setDatatableData,
    setDeletedDatatableData,
    setEditedDatatableData,
    setSelectedRows,
    selectedRows,
    getPrimaryKeyValue,
    originalRowMap,
    containsField,
    primaryKey,
  ]);

  const ActionToolbar = memo(
    ({
      disableColumnFilter,
      showQuickFilter,
      disableColumnMenu,
      rightContent,
      leftContent,
      hideAction = false,
      hideUpdate = false,
      hideDelete = false,
    }: {
      disableColumnFilter?: boolean;
      showQuickFilter?: boolean;
      disableColumnMenu?: boolean;
      rightContent?: ReactNode;
      leftContent?: ReactNode;
      hideAction?: boolean;
      hideUpdate?: boolean;
      hideDelete?: boolean;
    }) => {
      const hasActionRows = datatableData.some(
        (row) => '_action' in row && row._action !== 'raw',
      );

      const hasEdit = editedDatatableData?.some((editedRow) => {
        const primaryKeyValue = getPrimaryKeyValue(editedRow, primaryKey);

        const originalRow = originalRowMap.get(primaryKeyValue);

        if (!originalRow) return false;

        return Object.keys(editedRow)
          .filter((key) => key !== 'id' && key !== '_action')
          .some((key) => editedRow[key] !== originalRow[key]);
      });

      // Check if any row or cell is in edit mode
      const hasRowInEditMode = Object.values(rowModesModel).some(
        (model) => model.mode === GridRowModes.Edit,
      );
      const hasCellInEditMode = Object.values(cellModesModel).some(
        (cellModel) =>
          Object.values(cellModel).some(
            (cell) => cell.mode === GridCellModes.Edit,
          ),
      );

      const onEdit = hasRowInEditMode || hasCellInEditMode;

      const handleDoneEdit = () => {
        setDoneEdit(true);
        resetRowModesToView(false);
        resetCellModesToView(false);
      };

      return (
        <GridToolbarContainer>
          {!disableColumnMenu && <GridToolbarColumnsButton />}
          {!disableColumnFilter && <GridToolbarFilterButton />}
          {(!disableColumnMenu || !disableColumnFilter) && leftContent && (
            <Divider
              orientation='vertical'
              sx={{
                height: '36px',
                display: { xs: 'none', sm: 'inline-block' },
              }}
            />
          )}
          {leftContent}

          {!hideAction && (
            <Stack
              direction='row'
              gap={1}
              alignItems='center'
              sx={{ button: { px: 1, columnGap: '4px' } }}
            >
              {!hideDelete && selectedRows.length > 0 && (
                <Button
                  onClick={handleDeleteRows}
                  startIcon={<Delete sx={{ color: '#FF5630' }} />}
                  sx={{ color: '#FF5630' }}
                >
                  Delete
                </Button>
              )}
              {!hideUpdate && !onEdit && (
                <Button
                  disabled={!hasActionRows || onEdit}
                  onClick={updateChanges}
                  startIcon={<Save />}
                >
                  Update
                </Button>
              )}
              {onEdit && (
                <>
                  <Button
                    variant='contained'
                    startIcon={<EditOutlined />}
                    onClick={handleDoneEdit}
                    sx={{ px: '16px!important' }}
                  >
                    Done Edit
                  </Button>
                </>
              )}
              <Button
                disabled={
                  onEdit || (!hasEdit && deletedDatatableData?.length <= 0)
                }
                onClick={discardAllChanges}
                startIcon={<Restore />}
              >
                Discard all changes
              </Button>
            </Stack>
          )}

          <Box sx={{ flexGrow: 1 }} />
          {rightContent}
          {showQuickFilter && <GridToolbarQuickFilter sx={{ ml: 'auto' }} />}
        </GridToolbarContainer>
      );
    },
  );

  // #endregion

  const dataTableKey = useMemo(() => {
    if (!processRow) return gridKey + `${String(primaryKey)}`;
    return `${gridKey}-${String(primaryKey)}-${hashData(datatableData)}`;
  }, [datatableData, processRow, gridKey, primaryKey]);

  const key = dataTableKey;

  return {
    // FILTER AND COLUMN VISIBILITY
    resetState,
    ctrlFilter: {
      filterModel,
      onFilterModelChange: handleFilterModelChange,
    },
    ctrlColVis: {
      columnVisibilityModel,
      onColumnVisibilityModelChange: handleColumnVisibilityModelChange,
    },
    ctrlFilterColVis: {
      filterModel,
      onFilterModelChange: handleFilterModelChange,
      columnVisibilityModel,
      onColumnVisibilityModelChange: handleColumnVisibilityModelChange,
    },
    // CELL EDIT
    cellEdit: {
      ...(processRow && { key }),
      isCellEditable,
      disableRowSelectionOnClick: true,
      processRowUpdate,
      onCellEditStop: handleCellEditStop,
      cellModesModel,
      onCellModesModelChange: handleCellModesModelChange,
      onCellClick: handleCellClick,
      getRowClassName,
      onRowSelectionModelChange: handleSelectionChange,
      rowSelectionModel: selectedRows,
    },
    cellModesModel,
    setCellToViewMode,
    resetCellModesToView,
    // ROW EDIT
    rowEdit: {
      ...(processRow && { key }),
      editMode: 'row' as GridEditMode,
      isCellEditable,
      disableRowSelectionOnClick: true,
      processRowUpdate,
      onRowEditStop: handleRowEditStop,
      rowModesModel,
      onRowModesModelChange: handleRowModesModelChange,
      onRowClick: handleRowEditStart,
      getRowClassName,
      onRowSelectionModelChange: handleSelectionChange,
      rowSelectionModel: selectedRows,
    },
    rowModesModel,
    resetRowModesToView,
    // ROW SELECTION
    rowSelection: {
      onRowSelectionModelChange: handleSelectionChange,
      rowSelectionModel: selectedRows,
    },
    clearSelectedRows,
    selectedRows,
    setSelectedRows,
    isRowSelectable,
    // DATA
    originalDatatableData,
    datatableData,
    setDatatableData,
    editedDatatableData,
    setEditedDatatableData,
    deletedDatatableData,
    setDeletedDatatableData,
    handleFilterData,
    handleAddItems,
    isDatatableDataValid,
    // TOOLBAR
    discardAllChanges,
    updateChanges,
    handleDeleteRows,
    // ACTION COLUMN
    handleDiscardChanges,
    handleDelete,
    // UI
    renderConfirmDialog,
    action: {
      column: actionColumn,
      toolbar: ActionToolbar,
      isRowSelectable,
    },
    getRowClassName,
  };
};

export default useControlledDatatable;
